import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';

type PendingSubmission = {
  id: number;
  domain: string;
  path_prefix: string;
  seed_url: string;
  submission_note: string | null;
  created_at: string;
  domain_verified_at: string | null;
  submitter: { name: string; email: string } | null;
};

type ApproveForm = {
  requiresLogin: boolean;
  loginHandler: string;
  loginUsername: string;
  loginPassword: string;
  skipBinaries: boolean;
};

const DEFAULT_FORM: ApproveForm = {
  requiresLogin: false,
  loginHandler: 'memberpress',
  loginUsername: '',
  loginPassword: '',
  skipBinaries: false,
};

type SiteGroup = { key: string; label: string; items: PendingSubmission[] };

// Group competing claims by site. The backend already orders by domain, then
// path, then verified-first, so we just preserve that order into groups.
function groupBySite(rows: PendingSubmission[]): SiteGroup[] {
  const groups: SiteGroup[] = [];
  const index = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.domain} ${r.path_prefix ?? ''}`;
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, label: `${r.domain}${r.path_prefix ?? ''}`, items: [] });
    }
    groups[index.get(key)!].items.push(r);
  }
  return groups;
}

export default function AdminSubmissions() {
  const { refreshReviewCounts } = useAuth();
  const [rows, setRows] = useState<PendingSubmission[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [form, setForm] = useState<ApproveForm>(DEFAULT_FORM);

  async function load() {
    const { data } = await api<{ data: PendingSubmission[] }>('/admin/submissions');
    setRows(data);
  }

  useEffect(() => { load(); }, []);

  function startApprove(id: number) {
    setApprovingId(id);
    setForm(DEFAULT_FORM);
    setError(null);
  }

  function cancelApprove() {
    setApprovingId(null);
    setForm(DEFAULT_FORM);
  }

  async function submitApprove(id: number, e: FormEvent) {
    e.preventDefault();
    setBusy(id);
    setError(null);

    const body: Record<string, unknown> = { skip_binaries: form.skipBinaries };
    if (form.requiresLogin) {
      body.requires_login = true;
      body.login_handler = form.loginHandler;
      body.login_username = form.loginUsername;
      body.login_password = form.loginPassword;
    }

    try {
      await api(`/admin/submissions/${id}/approve`, { method: 'POST', body });
      cancelApprove();
      // Reload — approving a claim auto-rejects the others for that site, so
      // the whole queue can shift.
      await load();
      await refreshReviewCounts();
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Approve failed (${err.status}).`);
      } else {
        setError('Approve failed.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function reject(id: number) {
    const reason = window.prompt('Reason for rejection (the submitter will see this):');
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      window.alert('A reason is required.');
      return;
    }
    setBusy(id);
    setError(null);
    try {
      await api(`/admin/submissions/${id}/reject`, {
        method: 'POST',
        body: { rejection_reason: trimmed },
      });
      await load();
      await refreshReviewCounts();
    } catch (err) {
      setError(err instanceof ApiError ? `Reject failed (${err.status}).` : 'Reject failed.');
    } finally {
      setBusy(null);
    }
  }

  const groups = rows ? groupBySite(rows) : [];

  return (
    <div className="card">
      <h2>Submission review queue</h2>
      <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
        Sites users have proposed for archiving, grouped by site. When more than
        one person requests the same site, they're competing claims — the
        <strong> owner-verified</strong> badge (DNS-proven ownership) is the
        signal for who has the best claim. Approving one claim creates the
        archive and auto-rejects the rest.
      </p>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

      {rows === null ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No submissions awaiting review.</p>
      ) : (
        groups.map((g) => (
          <div key={g.key} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <strong style={{ fontSize: 15, wordBreak: 'break-all' }}>{g.label}</strong>
              {g.items.length > 1 && (
                <span className="status-pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: 11 }}>
                  {g.items.length} competing claims — approving one rejects the rest
                </span>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Submitter</th>
                  <th>Claim</th>
                  <th>Submitted</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    isApproving={approvingId === row.id}
                    isBusy={busy === row.id}
                    form={form}
                    onForm={setForm}
                    onStartApprove={() => startApprove(row.id)}
                    onCancelApprove={cancelApprove}
                    onSubmitApprove={(e) => submitApprove(row.id, e)}
                    onReject={() => reject(row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}

type RowProps = {
  row: PendingSubmission;
  isApproving: boolean;
  isBusy: boolean;
  form: ApproveForm;
  onForm: (next: ApproveForm) => void;
  onStartApprove: () => void;
  onCancelApprove: () => void;
  onSubmitApprove: (e: FormEvent) => void;
  onReject: () => void;
};

function Row({
  row, isApproving, isBusy, form, onForm,
  onStartApprove, onCancelApprove, onSubmitApprove, onReject,
}: RowProps) {
  return (
    <>
      <tr>
        <td>
          {row.submitter ? (
            <>
              <div>{row.submitter.name}</div>
              <div className="muted" style={{ fontSize: 12 }}>{row.submitter.email}</div>
            </>
          ) : (
            <span className="muted">unknown</span>
          )}
        </td>
        <td style={{ wordBreak: 'break-word' }}>
          <div>
            {row.domain_verified_at ? (
              <span className="status-pill" title="Submitter proved domain ownership via DNS TXT record"
                style={{ background: '#dbeafe', color: '#1e40af', fontSize: 11 }}>
                ✓ owner-verified
              </span>
            ) : (
              <span className="status-pill" title="Domain ownership not proven"
                style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11 }}>
                unverified
              </span>
            )}
          </div>
          <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
            {row.seed_url}
          </div>
        </td>
        <td className="muted" style={{ whiteSpace: 'nowrap' }}>
          {new Date(row.created_at).toLocaleString()}
        </td>
        <td style={{ maxWidth: 320, wordBreak: 'break-word' }}>
          {row.submission_note ?? <span className="muted">—</span>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {!isApproving && (
            <>
              <button onClick={onStartApprove} disabled={isBusy}>Approve…</button>
              <button onClick={onReject} disabled={isBusy} className="secondary" style={{ marginLeft: 8 }}>
                Reject
              </button>
            </>
          )}
        </td>
      </tr>
      {isApproving && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--bg-muted)', padding: 16 }}>
            <form onSubmit={onSubmitApprove}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={form.requiresLogin}
                  onChange={(e) => onForm({ ...form, requiresLogin: e.target.checked })}
                />
                <span>This site requires login</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={form.skipBinaries}
                  onChange={(e) => onForm({ ...form, skipBinaries: e.target.checked })}
                />
                <span>Skip binaries &amp; images (don't fetch zip/pdf/media/images — text &amp; page chrome only)</span>
              </label>
              {form.requiresLogin && (
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                  <label htmlFor={`handler-${row.id}`}>Login type</label>
                  <select
                    id={`handler-${row.id}`}
                    value={form.loginHandler}
                    onChange={(e) => onForm({ ...form, loginHandler: e.target.value })}
                  >
                    <option value="memberpress">MemberPress (WordPress)</option>
                    <option value="xenforo">XenForo</option>
                    <option value="invision">Invision Community (IPS 4.x)</option>
                  </select>

                  <label htmlFor={`username-${row.id}`}>Username</label>
                  <input
                    id={`username-${row.id}`}
                    type="text"
                    value={form.loginUsername}
                    onChange={(e) => onForm({ ...form, loginUsername: e.target.value })}
                    autoComplete="off"
                    required
                  />

                  <label htmlFor={`password-${row.id}`}>Password</label>
                  <input
                    id={`password-${row.id}`}
                    type="password"
                    value={form.loginPassword}
                    onChange={(e) => onForm({ ...form, loginPassword: e.target.value })}
                    autoComplete="off"
                    required
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={isBusy}>
                  {isBusy ? 'Approving…' : (form.requiresLogin ? 'Test login & approve' : 'Approve')}
                </button>
                <button type="button" onClick={onCancelApprove} disabled={isBusy} className="secondary">
                  Cancel
                </button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}
